import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import * as moment from 'moment';
import { NotificationSettings } from '../interfaces/notification-settings.interface';
import { NotificationTokenInterface } from '../interfaces/notification-token.interface';
import {
  Notification,
  NotificationType,
} from '../interfaces/notification.interface';
import { NotificationList } from '../interfaces/notificationList.interface';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly NOTIFICATIONS_PER_PAGE: number = 10;
  private currentNotificationPage: number = 0;
  public areAllNotificationsLoaded: boolean = false;
  public notifications: Notification[] = [];
  public lastReadDate: Date;
  public areThereNewNotifications: boolean = false;

  //settings
  public allowEmailNotifications: boolean = true;
  public allowPushNotifications: boolean = true;
  public allowSMSNotifications: boolean = true;
  public isLoaded: boolean = false;
  public areSettingsChanged: boolean = false;

  get areThereNoNotifications() {
    return this.notifications.length === 0;
  }

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private storageService: StorageService
  ) {
    if (Capacitor.isNativePlatform()) {
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          this.areThereNewNotifications = true;
        }
      );
    }
  }

  public async checkForNewNotifications(): Promise<void> {
    const lastReadDateISOString = this.storageService.get(
      'notificationsLastReadDate'
    );
    if (lastReadDateISOString) {
      this.lastReadDate = new Date(lastReadDateISOString);
    }

    const notifications = await this.getNotifications();

    for (
      let notificationIndex = 0;
      notificationIndex < notifications.powiadomienia.length;
      ++notificationIndex
    ) {
      if (
        this.isNotificationUnread(
          new Date(notifications.powiadomienia[notificationIndex].data)
        )
      ) {
        this.areThereNewNotifications = true;
        break;
      }
    }
  }

  public async loadNotifications(resetList: boolean = false): Promise<void> {
    if (resetList) {
      this.notifications = [];
      this.currentNotificationPage = 0;
      this.areAllNotificationsLoaded = false;
    }

    const notificationsJson = await this.getNotifications();
    const newNotifications = notificationsJson.powiadomienia;
    if (newNotifications.length < this.NOTIFICATIONS_PER_PAGE) {
      this.areAllNotificationsLoaded = true;
    }

    newNotifications.forEach((notification) => {
      notification.data = new Date(notification.data);
      notification.shortenedDate = moment(notification.data).format(
        'DD.MM.YYYY'
      );
      notification.isArchived = false;
    });

    this.notifications = this.notifications.concat(newNotifications);
  }

  private async getNotifications(): Promise<NotificationList> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;

    let url = api + '/api/Powiadomienia/PobierzPowiadomienia?';
    let urlParams = new HttpParams();
    urlParams = urlParams.set('Jezyk', lang);
    urlParams = urlParams.set('strona', this.currentNotificationPage);
    urlParams = urlParams.set('iloscElementow', this.NOTIFICATIONS_PER_PAGE);
    url = url + urlParams.toString();

    return this.http
      .request<NotificationList>('get', url, {
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public async loadMoreNotifications(): Promise<void> {
    this.currentNotificationPage += 1;
    await this.loadNotifications();
  }

  public updateLastReadDate(): void {
    if (this.notifications.length === 0) {
      return;
    }

    this.lastReadDate = this.notifications[0].data;
    this.storageService.set(
      'notificationsLastReadDate',
      this.lastReadDate.toISOString()
    );
  }

  public async loadNotificationSettings(): Promise<void> {
    const settingsJson = await this.getNotificationSettings();
    this.allowEmailNotifications = !settingsJson.niePowiadamiajEmail;
    this.allowPushNotifications = !settingsJson.niePowiadamiajPush;
    this.allowSMSNotifications = !settingsJson.niePowiadamiajSMS;
    this.isLoaded = true;
  }

  private async getNotificationSettings(): Promise<NotificationSettings> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;

    return this.http
      .get<NotificationSettings>(api + '/api/Ustawienia/Powiadomienia', {
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public isNotificationUnread(notificationDate: Date): boolean {
    if (this.lastReadDate == null) {
      return true;
    } else if (this.lastReadDate < notificationDate) {
      return true;
    } else {
      return false;
    }
  }

  public updateNotificationSettings(): Promise<void> {
    if (!this.isLoaded || !this.areSettingsChanged) {
      return;
    }

    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;
    const body: NotificationSettings = {
      niePowiadamiajSMS: !this.allowSMSNotifications,
      niePowiadamiajEmail: !this.allowEmailNotifications,
      niePowiadamiajPush: !this.allowPushNotifications,
      niePowiadamiajSMS_Odbiory: !this.allowSMSNotifications,
      niePowiadamiajPush_Odbiory: !this.allowPushNotifications,
    };

    this.http
      .request<any>('post', api + '/api/Ustawienia/UstawPowiadomienia', {
        body: body,
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise()
      .then(() => {
        this.areSettingsChanged = false;
      });
  }

  public sendNotificationToken(data: NotificationTokenInterface): Promise<any> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');

    return this.http
      .request<NotificationTokenInterface>(
        'post',
        `${this.apiService.api}/api/Ustawienia/ZarejestrujUrzadzenie`,
        {
          body: data,
          params: {
            Jezyk: lang,
          },
          headers: {
            Token: token,
          },
        }
      )
      .toPromise();
  }
}
